// A Scrivener document in script mode (`<TextMode>Script</TextMode>`) as Fountain (`ScrivenerScript`, SuiteImport).
// Scrivener keeps no element names in the RTF: each element is a paragraph format (Scrivener's Screenplay format puts a
// cue about 2.2 in in from the text margin, a parenthetical 1.6 in, dialogue 1 in, a transition flush right), so
// elements are read back from indents and alignment, with the text deciding where the format can't (INT./EXT.,
// brackets, capitals).
import type { FountainElement } from '../contract.ts';
import { isCharacterCue, isSceneHeading } from '../fountain.ts';
import { buildFountain, type Written } from '../fountainBuilder.ts';
import type { ScriptFormat } from '../scriptFormat.ts';
import { rtfParagraphs, type RTFParagraph } from './rtf.ts';

export function scrivenerScriptFountain(rtf: Uint8Array, format: ScriptFormat = 'screen'): string {
  const paragraphs = rtfParagraphs(rtf);
  return buildFountain(format === 'stage-us' ? classifyUSPlay(paragraphs) : format === 'stage-uk' ? classifyUKPlay(paragraphs)
    : format === 'radio' ? classifyRadio(paragraphs) : classifyScript(paragraphs));
}

/** `ScrivenerScript.format(named:)`: Scrivener's "Stage Play (US)" and "Stage Play (UK)" (or a writer's own format named
 *  for a stage play) are plays, its "BBC Radio Scene Style" (and "BBC Taped Drama", or any format named for radio) a radio
 *  play, anything else a screenplay. */
export function scriptFormatNamed(name: string | null | undefined): ScriptFormat {
  const n = name?.toLowerCase();
  if (!n) return 'screen';
  if (n.includes('radio') || n.includes('taped drama')) return 'radio';
  if (!n.includes('stage')) return 'screen';
  return n.includes('uk') || n.includes('british') ? 'stage-uk' : 'stage-us';
}

export function classifyScript(paragraphs: RTFParagraph[]): Written[] {
  const result: Written[] = [];
  let previous: FountainElement | null = null;
  for (const paragraph of paragraphs) {
    const line = paragraph.text.replace(/^[ \t]+|[ \t]+$/g, '');
    if (!line) {
      // A blank paragraph ends a speech: what follows isn't dialogue.
      if (previous === 'dialogue' || previous === 'parenthetical') previous = 'action';
      continue;
    }
    const element = scriptElement(line, paragraph.indent, paragraph.align, previous);
    const emphasised = emphasis(paragraph, line);
    result.push({ element, text: element === 'sceneHeading' || element === 'character' || element === 'transition' ? line : emphasised });
    previous = element;
  }
  return result;
}

const isUppercase = (s: string) => s === s.toUpperCase();

export function scriptElement(line: string, indent: number, align: RTFParagraph['align'], previous: FountainElement | null): FountainElement {
  const speaking = previous === 'character' || previous === 'parenthetical' || previous === 'dialogue';
  if (align === 'right') return 'transition';
  if (align === 'center') return 'centered';
  if (line.startsWith('(') && line.endsWith(')') && (speaking || (indent >= 1.2 && indent < 2.0))) return 'parenthetical';
  if (indent >= 1.9 && isCharacterCue(line)) return 'character';
  if (indent >= 3.5 && isUppercase(line)) return 'transition';
  if (indent >= 0.6 && indent < 1.9 && speaking) return 'dialogue';
  if (indent < 0.6 && isSceneHeading(line)) return 'sceneHeading';
  if (indent < 0.6 && isUppercase(line) && line.endsWith('TO:')) return 'transition';
  return 'action';
}

/** The paragraph's italic, bold and underline as Fountain emphasis. */
function emphasis(paragraph: Pick<RTFParagraph, 'runs'>, line: string, ignoringItalic = false): string {
  let out = '';
  for (const r of paragraph.runs) {
    let run = r.text.replaceAll('\n', '');
    if (!run.trim()) { out += run; continue; }
    const lead = run.match(/^ */)![0], trail = run.match(/ *$/)![0];
    run = run.trim();
    if (r.underline) run = `_${run}_`;
    if (r.bold) run = `**${run}**`;
    if (r.italic && !ignoringItalic) run = `*${run}*`;
    out += lead + run + trail;
  }
  return out.trim() === '' ? line : out.trim();
}

// Stage plays (`ScrivenerScript` classifyUSPlay/classifyUKPlay).

const NUMBER = String.raw`(\[N\]|\d+|[IVXLC]+|[A-Z][A-Za-z]*)`;
const UPPER_HEADING = new RegExp(String.raw`^(ACT|SCENE)(\s+${NUMBER})?([\s,.:–—-]+SCENE(\s+${NUMBER})?)?\.?$`);
const TITLE_HEADING = new RegExp(String.raw`^(Act|Scene)\s+${NUMBER}\.?$`);

/** `isActOrSceneHeading`: Scrivener's act and scene headings ("ACT [N]", "ACT I⇥SCENE 1", "Scene 3"), left out: the
 *  binder's folders and documents are the acts and scenes, and compile numbers them. */
export function isActOrSceneHeading(line: string): boolean {
  const t = line.replaceAll('\t', ' ');
  return UPPER_HEADING.test(t) || TITLE_HEADING.test(t);
}

const BRACKETED = /^([*_]*)\((.*)\)([*_]*)$/s;
/** `unbracketed`: without one pair of brackets round it, emphasis outside them kept. */
export function unbracketed(s: string): string {
  return BRACKETED.test(s) ? s.replace(BRACKETED, '$1$2$3').replace(/^[ \t]+|[ \t]+$/g, '') : s;
}
const bracketed = (line: string) => line.startsWith('(') && line.endsWith(')');
const trimmed = (s: string) => s.replace(/^[ \t]+|[ \t]+$/g, '');

/** Scrivener's Stage Play (US) format (see the Swift for the indents). */
export function classifyUSPlay(all: RTFParagraph[]): Written[] {
  const ps = all.filter((p) => trimmed(p.text) !== '');
  const result: Written[] = [];
  let speaker: string | null = null, speaking = false, continued = false;
  const isDialogue = (p: RTFParagraph | undefined) => !!p && p.indent < 1.0 && !bracketed(trimmed(p.text)) && p.align !== 'right';
  ps.forEach((p, i) => {
    const line = trimmed(p.text);
    const next = ps[i + 1];
    const emphasised = emphasis(p, line);
    if (isActOrSceneHeading(line)) { speaking = false; speaker = null; return; }
    if (p.align === 'right') { result.push({ element: 'transition', text: line.toUpperCase() }); speaking = false; return; }
    if (p.align === 'center') { result.push({ element: 'centered', text: emphasised }); speaking = false; return; }
    if (bracketed(line) || (line.startsWith('(') && p.indent >= 1.0)) {
      const words = unbracketed(emphasised);
      if (p.indent >= 2.5 && !speaking) {
        const labelled = ['setting:', 'at rise:'].some((l) => words.toLowerCase().startsWith(l));
        result.push({ element: 'action', text: labelled ? words : 'Setting: ' + words });
      } else if (speaking && isDialogue(next) && [...line].length <= 60 && !line.slice(1).includes('(')) {
        result.push({ element: 'parenthetical', text: emphasised });
      } else {
        result.push({ element: 'action', text: words });
        continued = speaking && isDialogue(next);
        speaking = false;
      }
      return;
    }
    if (p.indent >= 2.5 && [...line].length <= 40) {
      speaker = line.toUpperCase();
      result.push({ element: 'character', text: speaker });
      speaking = true;
      return;
    }
    if (p.indent < 1.0 && (speaking || continued)) {
      if (continued && speaker) result.push({ element: 'character', text: speaker });
      continued = false;
      speaking = true;
      result.push({ element: 'dialogue', text: emphasised });
      return;
    }
    result.push({ element: 'action', text: unbracketed(emphasised) });
    speaking = false;
    continued = false;
  });
  return result;
}

/** The runs from `offset` on. */
function runsFrom(p: RTFParagraph, offset: number): RTFParagraph['runs'] {
  const out: RTFParagraph['runs'] = [];
  let at = 0;
  for (const r of p.runs) {
    const end = at + r.text.length;
    if (end > offset) out.push({ ...r, text: r.text.slice(Math.max(0, offset - at)) });
    at = end;
  }
  return out;
}

const isItalic = (p: Pick<RTFParagraph, 'runs'>) => p.runs.every((r) => !/\p{L}/u.test(r.text) || r.italic);

/** Scrivener's Stage Play (UK) format: "NAME:⇥speech" hanging at 1.5 in, Dialogue (Cont.) at 1.5 in without the name,
 *  Scene Action in italics at the margin; a "(Pause)" opening a speech is its parenthetical. */
/** `ScrivenerScript.classifyRadio`: Scrivener's BBC Radio Scene Style, in inches from the text margin — "SCENE n." and the
 *  technical directions at about 1.5 in, underlined (the scene numbers dropped: compile numbers the scenes); "NAME:⇥speech"
 *  as a UK play's, a speech's further paragraphs hanging at the speech's tab; a voice direction in brackets at a speech's
 *  start its parenthetical; END. dropped. */
export function classifyRadio(all: RTFParagraph[]): Written[] {
  const result: Written[] = [];
  let speaker: string | null = null;
  const speech = (name: string, runs: RTFParagraph['runs']) => {
    result.push({ element: 'character', text: name });
    let words = emphasis({ runs }, runs.map((r) => r.text).join('').trim());
    const paren = /^[*_]*\([^)]*\)[*_]*/.exec(words);
    if (paren) {
      result.push({ element: 'parenthetical', text: paren[0].replace(/^[*_]+|[*_]+$/g, '') });
      words = trimmed(words.slice(paren[0].length));
    }
    if (words) result.push({ element: 'dialogue', text: words });
  };
  for (const p of all) {
    const line = trimmed(p.text);
    if (!line) continue;
    if (isActOrSceneHeading(trimmed(p.rawText)) || line.toUpperCase() === 'END.' || line.toUpperCase() === 'END') { speaker = null; continue; }
    const tab = p.rawText.indexOf('\t');
    if (tab >= 0 && p.firstIndent < 1.25) {
      const name = p.rawText.slice(0, tab).trim().replace(/^:+|:+$/g, '').trim();
      const length = [...name].length;
      if (length >= 1 && length <= 40 && /\p{L}/u.test(name) && !/[.!?]/.test(name)) {
        speaker = name.toUpperCase();
        speech(speaker, runsFrom(p, tab + 1));
        continue;
      }
    }
    if (p.align === 'center') { result.push({ element: 'centered', text: emphasis(p, line) }); continue; }
    // A speech goes on in a paragraph of its own at the speech's tab; technical directions sit further in.
    if (p.firstIndent >= 0.75 && p.firstIndent < 1.25 && speaker) { speech(speaker, p.runs); continue; }
    speaker = null;
    // The layout underlines and capitalises directions itself: the text as written, without that underline.
    result.push({ element: 'action', text: unbracketed(emphasis(p, line).replaceAll('_', '')) });
  }
  return result;
}

export function classifyUKPlay(all: RTFParagraph[]): Written[] {
  const result: Written[] = [];
  let speaker: string | null = null;
  const speech = (name: string, runs: RTFParagraph['runs']) => {
    result.push({ element: 'character', text: name });
    let words = emphasis({ runs }, runs.map((r) => r.text).join('').trim());
    const paren = /^[*_]*\([^)]*\)[*_]*/.exec(words);
    if (paren) {
      result.push({ element: 'parenthetical', text: paren[0].replace(/^[*_]+|[*_]+$/g, '') });
      words = trimmed(words.slice(paren[0].length));
    }
    if (words) result.push({ element: 'dialogue', text: words });
  };
  for (const p of all) {
    const line = trimmed(p.text);
    if (!line) continue;
    if (isActOrSceneHeading(trimmed(p.rawText))) { speaker = null; continue; }
    const tab = p.rawText.indexOf('\t');
    if (tab >= 0) {
      const name = p.rawText.slice(0, tab).trim().replace(/^:+|:+$/g, '').trim();
      const length = [...name].length;
      if (length >= 1 && length <= 40 && /\p{L}/u.test(name) && !/[.!?]/.test(name)) {
        speaker = name.toUpperCase();
        speech(speaker, runsFrom(p, tab + 1));
        continue;
      }
    }
    if (p.align === 'right') { result.push({ element: 'transition', text: line.toUpperCase() }); continue; }
    if (p.align === 'center') { result.push({ element: 'centered', text: emphasis(p, line) }); continue; }
    if (p.firstIndent >= 1.0 && !isItalic(p) && speaker) { speech(speaker, p.runs); continue; }
    result.push({ element: 'action', text: unbracketed(emphasis(p, line, isItalic(p))) });
  }
  return result;
}
